/**
 * Chart Showcase for Testing Chart Colors
 *
 * Test page to visualize chart color system and compare different libraries.
 * Useful for evaluating Recharts vs Nivo vs Tremor for issue #104.
 */

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { BarChart, Bar, PieChart, Pie, Cell, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { chartColors, getCategoryColor, getSeriesColor } from "@/utils/chartColors";

const categoryData = [
  { category: "food", name: "Food & Groceries", value: 450, fill: getCategoryColor("food") },
  { category: "household", name: "Household Items", value: 320, fill: getCategoryColor("household") },
  { category: "health", name: "Health & Wellness", value: 180, fill: getCategoryColor("health") },
  { category: "personal", name: "Personal Care", value: 95, fill: getCategoryColor("personal") },
  { category: "other", name: "Other Items", value: 55, fill: getCategoryColor("other") },
];

const trendData = [
  { month: "Jan", value: 1200 },
  { month: "Feb", value: 1450 },
  { month: "Mar", value: 980 },
  { month: "Apr", value: 1680 },
  { month: "May", value: 1420 },
  { month: "Jun", value: 1890 },
];

const multiSeriesData = [
  { month: "Jan", food: 450, household: 320, health: 180 },
  { month: "Feb", food: 520, household: 380, health: 220 },
  { month: "Mar", food: 390, household: 290, health: 150 },
  { month: "Apr", food: 610, household: 420, health: 250 },
  { month: "May", food: 480, household: 350, health: 190 },
  { month: "Jun", food: 680, household: 470, health: 280 },
];

export default function ChartShowcase() {
  return (
    <div className="container mx-auto p-8 space-y-12 max-w-6xl">
      <div className="space-y-2">
        <h1 className="text-4xl font-bold">Chart Color System</h1>
        <p className="text-muted-foreground">
          Test page for chart colors using centralized utility
        </p>
        <p className="text-sm text-muted-foreground">
          Issue #141 - Centralized chart colors | Issue #104 - Chart library evaluation
        </p>
      </div>

      <Separator />

      {/* Color Swatches */}
      <section className="space-y-6">
        <div>
          <h2 className="text-2xl font-semibold mb-2">Category Color Swatches</h2>
          <p className="text-sm text-muted-foreground">
            All category colors from centralized utility
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {Object.entries(chartColors.categories).map(([key, color]) => (
            <div key={key} className="space-y-2">
              <div
                className="h-24 rounded-lg border"
                style={{ backgroundColor: color }}
              />
              <div>
                <p className="text-sm font-medium capitalize">{key}</p>
                <p className="text-xs text-muted-foreground font-mono">{color}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <Separator />

      {/* Pie Chart */}
      <section className="space-y-6">
        <div>
          <h2 className="text-2xl font-semibold mb-2">Pie Chart - Category Breakdown</h2>
          <p className="text-sm text-muted-foreground">
            Using <code className="text-xs bg-muted px-1 rounded">getCategoryColor()</code> for each category
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Spending by Category</CardTitle>
            <CardDescription>
              Each slice uses <code className="text-xs">getCategoryColor(category)</code>
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={categoryData}
                    cx="50%"
                    cy="50%"
                    outerRadius={120}
                    dataKey="value"
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    labelLine={false}
                  >
                    {categoryData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.fill} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </section>

      <Separator />

      {/* Bar Chart */}
      <section className="space-y-6">
        <div>
          <h2 className="text-2xl font-semibold mb-2">Bar Chart - Spending Trend</h2>
          <p className="text-sm text-muted-foreground">
            Using <code className="text-xs bg-muted px-1 rounded">chartColors.primary</code>
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Monthly Spending Trend</CardTitle>
            <CardDescription>
              Bar color uses <code className="text-xs">chartColors.primary</code>
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={trendData}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                  <XAxis dataKey="month" fontSize={12} tickLine={false} axisLine={false} />
                  <YAxis fontSize={12} tickLine={false} axisLine={false} tickFormatter={(value) => `$${value}`} />
                  <Tooltip />
                  <Bar dataKey="value" fill={chartColors.primary} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </section>

      <Separator />

      {/* Multi-Series Line Chart */}
      <section className="space-y-6">
        <div>
          <h2 className="text-2xl font-semibold mb-2">Multi-Series Line Chart</h2>
          <p className="text-sm text-muted-foreground">
            Using <code className="text-xs bg-muted px-1 rounded">getSeriesColor(index)</code> for each line
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Category Trends Over Time</CardTitle>
            <CardDescription>
              Each line uses <code className="text-xs">getSeriesColor(0, 1, 2)</code>
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={multiSeriesData}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                  <XAxis dataKey="month" fontSize={12} tickLine={false} axisLine={false} />
                  <YAxis fontSize={12} tickLine={false} axisLine={false} tickFormatter={(value) => `$${value}`} />
                  <Tooltip />
                  <Line
                    type="monotone"
                    dataKey="food"
                    stroke={getSeriesColor(0)}
                    strokeWidth={2}
                    dot={{ fill: getSeriesColor(0) }}
                  />
                  <Line
                    type="monotone"
                    dataKey="household"
                    stroke={getSeriesColor(1)}
                    strokeWidth={2}
                    dot={{ fill: getSeriesColor(1) }}
                  />
                  <Line
                    type="monotone"
                    dataKey="health"
                    stroke={getSeriesColor(2)}
                    strokeWidth={2}
                    dot={{ fill: getSeriesColor(2) }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </section>

      <Separator />

      {/* Code Examples */}
      <section className="space-y-6">
        <div>
          <h2 className="text-2xl font-semibold mb-2">Usage Examples</h2>
          <p className="text-sm text-muted-foreground">
            How to use the centralized color utility
          </p>
        </div>

        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Category Colors (Pie Chart)</CardTitle>
            </CardHeader>
            <CardContent>
              <pre className="text-xs bg-muted p-4 rounded-lg overflow-x-auto">
{`import { getCategoryColor } from "@/utils/chartColors";

const data = [
  { category: "food", value: 450, fill: getCategoryColor("food") },
  { category: "health", value: 180, fill: getCategoryColor("health") },
];

<Pie data={data}>
  {data.map((entry, index) => (
    <Cell key={index} fill={entry.fill} />
  ))}
</Pie>`}
              </pre>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Primary Color (Bar Chart)</CardTitle>
            </CardHeader>
            <CardContent>
              <pre className="text-xs bg-muted p-4 rounded-lg overflow-x-auto">
{`import { chartColors } from "@/utils/chartColors";

<Bar dataKey="value" fill={chartColors.primary} />`}
              </pre>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Multi-Series (Line Chart)</CardTitle>
            </CardHeader>
            <CardContent>
              <pre className="text-xs bg-muted p-4 rounded-lg overflow-x-auto">
{`import { getSeriesColor } from "@/utils/chartColors";

<Line dataKey="series1" stroke={getSeriesColor(0)} />
<Line dataKey="series2" stroke={getSeriesColor(1)} />
<Line dataKey="series3" stroke={getSeriesColor(2)} />`}
              </pre>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
}
